import React, { useState, useCallback, useMemo } from 'react';
import { diffWords, diffLines, Change } from 'diff';
import Privacy from './Privacy';
import Terms from './Terms';
import Contact from './Contact';
import './App.css';
import './LegalPages.css';

interface DiffResult {
  words: Change[];
  lines: Change[];
}

type ViewMode = 'side-by-side' | 'inline';
type DiffLevel = 'word' | 'line' | 'both';
type CopyStatus = 'idle' | 'added' | 'removed' | 'all';

const App: React.FC = () => {
  const [leftText, setLeftText] = useState('');
  const [rightText, setRightText] = useState('');
  const [darkMode, setDarkMode] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('side-by-side');
  const [diffLevel, setDiffLevel] = useState<DiffLevel>('both');
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
  const [currentPage, setCurrentPage] = useState<'home' | 'privacy' | 'terms' | 'contact'>('home');

  const diffResult: DiffResult = useMemo(() => {
    return {
      words: diffWords(leftText, rightText),
      lines: diffLines(leftText, rightText)
    };
  }, [leftText, rightText]);

  React.useEffect(() => {
  const handleHashChange = () => {
    const hash:any = window.location.hash.slice(1);
    if (['home', 'privacy', 'terms', 'contact'].includes(hash)) {
      setCurrentPage(hash || 'home');
    }
  };
  handleHashChange();
  window.addEventListener('hashchange', handleHashChange);
  return () => window.removeEventListener('hashchange', handleHashChange);
}, []);

  const handleFileUpload = useCallback((side: 'left' | 'right', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (side === 'left') {
        setLeftText(text);
      } else {
        setRightText(text);
      }
    };
    reader.readAsText(file);
  }, []);

  const copyChanges = useCallback((type: 'added' | 'removed' | 'all') => {
    let textToCopy = '';

    diffResult.words.forEach(part => {
      if (type === 'all') {
        textToCopy += part.value;
      } else if (type === 'added' && part.added) {
        textToCopy += part.value;
      } else if (type === 'removed' && part.removed) {
        textToCopy += part.value;
      }
    });

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopyStatus(type);
      setTimeout(() => setCopyStatus('idle'), 2000);
    });
  }, [diffResult.words]);

  const clearAll = useCallback(() => {
    setLeftText('');
    setRightText('');
  }, []);

  const renderDiffText = (changes: Change[], isWordLevel: boolean) => {
    return changes.map((part, index) => {
      let className = '';
      if (part.added) className = 'diff-added';
      else if (part.removed) className = 'diff-removed';

      return (
        <span key={index} className={className}>
          {part.value}
        </span>
      );
    });
  };

  const stats = useMemo(() => {
    const added = diffResult.words.filter(p => p.added).reduce((acc, p) => acc + p.value.length, 0);
    const removed = diffResult.words.filter(p => p.removed).reduce((acc, p) => acc + p.value.length, 0);
    const unchanged = diffResult.words.filter(p => !p.added && !p.removed).reduce((acc, p) => acc + p.value.length, 0);

    return { added, removed, unchanged, total: added + removed + unchanged };
  }, [diffResult.words]);

  // Add this BEFORE your main return statement
if (currentPage === 'privacy') return <Privacy />;
if (currentPage === 'terms') return <Terms />;
if (currentPage === 'contact') return <Contact />;

  return (
    <div className={`app ${darkMode ? 'dark' : 'light'}`}>
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="2" width="12" height="28" rx="2" fill="currentColor" opacity="0.6"/>
                <rect x="18" y="2" width="12" height="28" rx="2" fill="currentColor" opacity="0.8"/>
                <path d="M10 10 L22 10 M10 16 L22 16 M10 22 L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="logo-text">
              <h1>TextCompare.in</h1>
              <p className="tagline">Compare Text Instantly</p>
            </div>
          </div>
          <div className="header-controls">
            <button 
              className="theme-toggle-btn"
              onClick={() => setDarkMode(!darkMode)}
              title="Toggle dark mode"
            >
              {darkMode ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/>
                  <line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/>
                  <line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="toolbar">
        <div className="toolbar-section">
          <button 
            className={`toolbar-btn view-btn ${viewMode === 'side-by-side' ? 'active' : ''}`}
            onClick={() => setViewMode('side-by-side')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="18" rx="1"/>
              <rect x="14" y="3" width="7" height="18" rx="1"/>
            </svg>
            <span>Side by Side</span>
          </button>
          <button 
            className={`toolbar-btn view-btn ${viewMode === 'inline' ? 'active' : ''}`}
            onClick={() => setViewMode('inline')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
            </svg>
            <span>Unified</span>
          </button>
        </div>

        <div className="toolbar-section">
          <button 
            className={`toolbar-btn diff-btn ${diffLevel === 'word' ? 'active' : ''}`}
            onClick={() => setDiffLevel('word')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h16M4 12h16M4 17h10"/>
            </svg>
            <span>Word</span>
          </button>
          <button 
            className={`toolbar-btn diff-btn ${diffLevel === 'line' ? 'active' : ''}`}
            onClick={() => setDiffLevel('line')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
            <span>Line</span>
          </button>
          <button 
            className={`toolbar-btn diff-btn ${diffLevel === 'both' ? 'active' : ''}`}
            onClick={() => setDiffLevel('both')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <path d="M9 9h6M9 15h6"/>
            </svg>
            <span>Both</span>
          </button>
        </div>

        <div className="toolbar-section">
          <button 
            className={`toolbar-btn action-btn copy-btn ${copyStatus === 'added' ? 'copied' : ''}`}
            onClick={() => copyChanges('added')}
          >
            {copyStatus === 'added' ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                  <rect x="8" y="2" width="8" height="4" rx="1"/>
                  <path d="M12 11v6M9 14l3 3 3-3" stroke="#4ec9b0"/>
                </svg>
                <span>Copy Added</span>
              </>
            )}
          </button>
          <button 
            className={`toolbar-btn action-btn copy-btn ${copyStatus === 'removed' ? 'copied' : ''}`}
            onClick={() => copyChanges('removed')}
          >
            {copyStatus === 'removed' ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>Copied!</span>
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                  <rect x="8" y="2" width="8" height="4" rx="1"/>
                  <path d="M9 14l3-3 3 3M12 11v6" stroke="#f48771"/>
                </svg>
                <span>Copy Removed</span>
              </>
            )}
          </button>
          <button className="toolbar-btn action-btn danger-btn" onClick={clearAll}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              <line x1="10" y1="11" x2="10" y2="17"/>
              <line x1="14" y1="11" x2="14" y2="17"/>
            </svg>
            <span>Clear All</span>
          </button>
        </div>
      </div>

      <div className="stats-bar">
        <div className="stat stat-added">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          <span className="stat-label">Added:</span>
          <span className="stat-value added">{stats.added} chars</span>
        </div>
        <div className="stat stat-removed">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          <span className="stat-label">Removed:</span>
          <span className="stat-value removed">{stats.removed} chars</span>
        </div>
        <div className="stat stat-unchanged">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <span className="stat-label">Unchanged:</span>
          <span className="stat-value">{stats.unchanged} chars</span>
        </div>
      </div>

      {viewMode === 'side-by-side' ? (
        <div className="editor-container side-by-side">
          <div className="editor-pane">
            <div className="pane-header">
              <div className="pane-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span>Original Text</span>
              </div>
              <label className="file-upload-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Upload File</span>
                <input 
                  type="file" 
                  accept=".txt" 
                  onChange={(e) => handleFileUpload('left', e)}
                />
              </label>
            </div>
            <textarea
              className="editor"
              value={leftText}
              onChange={(e) => setLeftText(e.target.value)}
              placeholder="Paste your original text here...&#10;&#10;💡 Supports: WhatsApp chats, code, documents, captions, any text!"
            />
          </div>

          <div className="editor-pane">
            <div className="pane-header">
              <div className="pane-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                <span>Modified Text</span>
              </div>
              <label className="file-upload-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Upload File</span>
                <input 
                  type="file" 
                  accept=".txt" 
                  onChange={(e) => handleFileUpload('right', e)}
                />
              </label>
            </div>
            <textarea
              className="editor"
              value={rightText}
              onChange={(e) => setRightText(e.target.value)}
              placeholder="Paste your modified text here..."
            />
          </div>
        </div>
      ) : (
        <div className="editor-container inline">
          <div className="editor-pane full-width">
            <div className="pane-header">
              <div className="pane-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span>Original Text</span>
              </div>
              <label className="file-upload-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Upload File</span>
                <input 
                  type="file" 
                  accept=".txt" 
                  onChange={(e) => handleFileUpload('left', e)}
                />
              </label>
            </div>
            <textarea
              className="editor"
              value={leftText}
              onChange={(e) => setLeftText(e.target.value)}
              placeholder="Paste your original text here..."
            />
          </div>
          <div className="editor-pane full-width">
            <div className="pane-header">
              <div className="pane-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                <span>Modified Text</span>
              </div>
              <label className="file-upload-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <span>Upload File</span>
                <input 
                  type="file" 
                  accept=".txt" 
                  onChange={(e) => handleFileUpload('right', e)}
                />
              </label>
            </div>
            <textarea
              className="editor"
              value={rightText}
              onChange={(e) => setRightText(e.target.value)}
              placeholder="Paste your modified text here..."
            />
          </div>
        </div>
      )}

      {(leftText || rightText) && (
        <div className="diff-container">
          <div className="diff-header">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <h2>Comparison Results</h2>
          </div>

          {(diffLevel === 'word' || diffLevel === 'both') && (
            <div className="diff-section">
              <h3>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 7h16M4 12h16M4 17h10"/>
                </svg>
                Word-Level Diff
              </h3>
              <div className="diff-output">
                {renderDiffText(diffResult.words, true)}
              </div>
            </div>
          )}

          {(diffLevel === 'line' || diffLevel === 'both') && (
            <div className="diff-section">
              <h3>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
                Line-Level Diff
              </h3>
              <div className="diff-output">
                {renderDiffText(diffResult.lines, false)}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="legend">
        <div className="legend-item">
          <span className="legend-color added"></span>
          <span>Added Text</span>
        </div>
        <div className="legend-item">
          <span className="legend-color removed"></span>
          <span>Removed Text</span>
        </div>
      </div>

      <footer className="footer">
        <div className="footer-content">
          <p>© 2025 TextCompare.in - All text comparison happens locally in your browser. Your data never leaves your device.</p>
          <div className="footer-links">
            <a href="#privacy">Privacy</a>
            <a href="#terms">Terms</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;